/**
 * Enhanced Axios Interceptor for automatic token refresh
 *
 * Industry-standard implementation following best practices from:
 * - Auth0, Okta, Netflix, Stripe
 * - axios-auth-refresh patterns
 *
 * Features:
 * - Automatic token injection from TokenManager
 * - Proactive refresh before token expiry
 * - Request queuing during token refresh (prevents race conditions)
 * - Retry failed requests after successful refresh
 * - Integration with AuthSync for cross-tab token sharing
 *
 * Security:
 * - Token stored only in memory (TokenManager)
 * - Refresh token in httpOnly cookie
 */

// Import singleton instances from shared singletons
import { tokenManager, authSync } from './singletons';

// Legacy token accessor functions - still supported for backward compatibility
let getAccessToken = () => tokenManager.getToken();
let setAccessToken = () => {};

// Track refresh state to prevent multiple simultaneous refreshes
let isRefreshing = false;
let failedQueue = [];

// The request-side proactive refresh gets its own flag. `isRefreshing` doubles as the signal
// that 401 responses should park in failedQueue, and only the response interceptor ever
// releases that queue - a request-side refresh that set it would strand every 401 that
// arrived meanwhile.
let proactiveRefreshInFlight = false;

/**
 * Configure token accessor functions (legacy support)
 * Called by AuthContext to connect interceptor to auth state
 * @param {Function} getter - Function to get current access token
 * @param {Function} setter - Function to set new access token
 */
export const configureTokenAccessor = (getter, setter) => {
  getAccessToken = getter;
  setAccessToken = setter;
};

/**
 * Process queued requests after token refresh
 * @param {Error|null} error - Error if refresh failed
 * @param {string|null} token - New token if refresh succeeded
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Setup axios interceptors for a given axios instance
 * @param {Object} axiosInstance - Axios instance to setup interceptors for
 * @param {Object} options - Configuration options
 * @param {Function} options.onLogout - Callback to execute on auth failure
 * @param {Function} options.onTokenRefresh - Callback when token is refreshed
 */
export const setupInterceptors = (axiosInstance, options = {}) => {
  const { onLogout, onTokenRefresh } = typeof options === 'function'
    ? { onLogout: options } // Legacy: single callback was logout
    : options;

  // Request interceptor - attach access token and check for proactive refresh
  axiosInstance.interceptors.request.use(
    async (config) => {
      // Get token from TokenManager (primary) or legacy accessor (fallback)
      let token = tokenManager.getToken() || getAccessToken();

      // Proactive refresh: if token is expiring soon, refresh before request. One request does
      // the refreshing; the rest go out on the token they already hold, which is still good for
      // the whole buffer period, instead of all stalling on the same round trip. TokenManager
      // already collapses concurrent calls into one network request, but a collapsed call is
      // still an awaited one.
      if (tokenManager.isTokenExpiringSoon() && !isRefreshing && !proactiveRefreshInFlight) {
        proactiveRefreshInFlight = true;
        try {
          console.log('[Axios] Proactive token refresh before request');
          token = await tokenManager.refreshToken();

          // Notify other tabs about the new token
          authSync.broadcastTokenRefresh(token, tokenManager.getSecondsUntilExpiry());

          // Call refresh callback if provided
          if (onTokenRefresh) {
            onTokenRefresh(token, tokenManager.getSecondsUntilExpiry());
          }
        } catch (e) {
          // Continue with current token, will be handled by response interceptor
          console.warn('[Axios] Proactive refresh failed, using existing token');
        } finally {
          proactiveRefreshInFlight = false;
        }
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add cache-busting headers to ALL requests
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      config.headers['Pragma'] = 'no-cache';
      config.headers['Expires'] = '0';

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle 401 errors and refresh token
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Only handle 401 errors that haven't been retried
      if (error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        console.log('[Axios] Request queued, waiting for token refresh');
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axiosInstance(originalRequest));
            },
            reject
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      console.log('[Axios] 401 received, attempting token refresh');

      try {
        // Use TokenManager for refresh (handles retries internally)
        const newToken = await tokenManager.refreshToken();

        // Update legacy accessor if configured
        setAccessToken(newToken);

        // Update axios default header
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

        // Notify other tabs about the new token
        authSync.broadcastTokenRefresh(newToken, tokenManager.getSecondsUntilExpiry());

        // Call refresh callback if provided
        if (onTokenRefresh) {
          onTokenRefresh(newToken, tokenManager.getSecondsUntilExpiry());
        }

        // Process queued requests with new token
        processQueue(null, newToken);

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);

      } catch (refreshError) {
        console.error('[Axios] Token refresh failed:', refreshError.message);

        // Process queued requests with error
        processQueue(refreshError, null);

        // Only logout if it's a true auth error (not network error)
        if (refreshError.isAuthError) {
          console.log('[Axios] Auth error, triggering logout');
          if (onLogout) {
            onLogout();
          }
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

export default setupInterceptors;
